import {
  Button,
  Center,
  Container,
  Heading,
  Stack,
  useDisclosure,
} from '@cardboard-ui/react';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { EditQuestionCard } from './components/EditQuestionCard';
import { EditForm_folderItem$key } from './__generated__/EditForm_folderItem.graphql';
import { EditForm_form$key } from './__generated__/EditForm_form.graphql';
import { AddQuestionCard } from './components/AddQuestionCard';
import { CantEditFormAlert } from './components/CantEditFormAlert';
import {
  EditForm_createQuestion_Mutation,
  EditForm_createQuestion_Mutation$variables,
} from './__generated__/EditForm_createQuestion_Mutation.graphql';
import { EditForm_orderQuestions_Mutation } from './__generated__/EditForm_orderQuestions_Mutation.graphql';
import { CloseFormControl } from './components/CloseFormControl';
import { t } from '@lingui/macro';
import { FormTemplatesModal } from './components/FormTemplatesModal';
import { TemplateIcon } from 'components/icons';
import { Card } from 'components/cards/Card';

const folderItemFragment = graphql`
  fragment EditForm_folderItem on FolderItem {
    publishState
  }
`;

const fragment = graphql`
  fragment EditForm_form on Form {
    id
    isClosed
    questions {
      id
      ...EditQuestionCard_formQuestion
    }
  }
`;

const createQuestionsMutation = graphql`
  mutation EditForm_createQuestion_Mutation(
    $formId: ID!
    $questions: [FormQuestionInput!]!
  ) {
    createMultipleQuestions(input: { formId: $formId, questions: $questions }) {
      form {
        ...EditForm_form
      }
    }
  }
`;

const orderFormQuestionsMutation = graphql`
  mutation EditForm_orderQuestions_Mutation(
    $input: FormsOrderQuestionsMutationInput!
  ) {
    orderQuestions(input: $input) {
      form {
        ...EditForm_form
      }
    }
  }
`;

interface EditFormProps {
  folderItem: EditForm_folderItem$key;
  form: EditForm_form$key;
}

export type CreateQuestionInput = Partial<
  EditForm_createQuestion_Mutation$variables['questions'][0]
>;

export const EditForm = ({ folderItem, form }: EditFormProps) => {
  const [orderQuestions, isOrderingQuestions] =
    useMutation<EditForm_orderQuestions_Mutation>(orderFormQuestionsMutation);

  const { publishState } = useFragment(folderItemFragment, folderItem);
  const { id: formId, isClosed, questions } = useFragment(fragment, form);
  const isDisabled = publishState !== 'DRAFT';
  const formHasQuestions = questions.length > 0;

  const {
    isOpen: isFormTemplatesModalOpen,
    onOpen: onFormTemplatesModalOpen,
    onClose: onFormTemplatesModalClose,
  } = useDisclosure();

  const [createQuestions, isCreateQuestionsLoading] =
    useMutation<EditForm_createQuestion_Mutation>(createQuestionsMutation);

  const [orderedQuestionIds, setOrderedQuestionIds] = useState<string[]>();

  useEffect(() => {
    if (questions) setOrderedQuestionIds(questions.map((q) => q.id));
  }, [questions]);

  const defaultInput = {
    required: false,
    text: t`Ask a question`,
  };

  const handleCreateQuestions = async (
    questions: CreateQuestionInput[],
    onCompleted?: () => void,
  ) => {
    if (questions.length === 0) {
      onCompleted && onCompleted();
    } else {
      const normalizedQuestions = questions.map((question) => ({
        ...defaultInput,
        ...question,
      }));

      createQuestions({
        variables: { formId, questions: normalizedQuestions },
        onCompleted,
      });
    }
  };

  const onDragEnd = (result: DropResult) => {
    const reorder = (
      list: Array<string>,
      startIndex: number,
      endIndex: number,
    ) => {
      const result = Array.from(list);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      return result;
    };

    if (!result.destination) {
      return;
    }

    const items = reorder(
      questions.map((question) => question.id),
      result.source.index,
      result.destination.index,
    );

    setOrderedQuestionIds(items);

    orderQuestions({
      variables: { input: { formId, questionIdsInOrder: items } },
    });
  };

  const orderedQuestions = orderedQuestionIds?.map((id) =>
    questions.find((q) => q.id === id),
  );

  return (
    <Container maxW="3xl">
      <Stack spacing={6} py={4}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="questions-droppable" direction="vertical">
            {(provided: DroppableProvided) => (
              <div>
                <Stack
                  spacing={4}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {
                    <CantEditFormAlert
                      isClosed={isClosed}
                      isPublished={publishState !== 'DRAFT'}
                    />
                  }
                  {publishState !== 'DRAFT' && !isClosed && (
                    <CloseFormControl formId={formId} />
                  )}
                  {!isDisabled && !formHasQuestions && (
                    <Stack alignItems="center">
                      <Heading fontSize="sm">{t`Use a template`}</Heading>
                      <Card px={2} py={4} isNotClickable w="100%">
                        <Center>
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<TemplateIcon color="blue.500" />}
                            onClick={onFormTemplatesModalOpen}
                          >{t`See form templates`}</Button>
                        </Center>
                      </Card>
                    </Stack>
                  )}
                  {orderedQuestions?.map((question, idx) =>
                    question ? (
                      <Draggable
                        key={question.id}
                        draggableId={question.id}
                        index={idx}
                        isDragDisabled={isOrderingQuestions || isDisabled}
                      >
                        {(provided) => (
                          <EditQuestionCard
                            question={question}
                            isDisabled={isDisabled}
                            formId={formId}
                            dragProps={provided}
                          />
                        )}
                      </Draggable>
                    ) : null,
                  )}
                </Stack>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {!isDisabled && (
          <AddQuestionCard
            isDisabled={isDisabled}
            handleCreateQuestions={handleCreateQuestions}
            isCreating={isCreateQuestionsLoading}
          />
        )}
      </Stack>

      <FormTemplatesModal
        isOpen={isFormTemplatesModalOpen}
        onClose={onFormTemplatesModalClose}
        onInstantiateTemplate={({ questions }, onCompleted) =>
          handleCreateQuestions(questions, onCompleted)
        }
      />
    </Container>
  );
};
